import * as Popover from '@radix-ui/react-popover';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronsUpDown } from 'lucide-react';
import { Session } from 'next-auth';
import { useState } from 'react';

import { Badge } from '@/components/badge';
import { UserDropdown } from '@/components/modules/user/dropdown';
import { Text } from '@/components/text';
import { UserAvatar } from '@/components/user-nav/user-avatar';
import { getActiveUserOrgsCount, getUserOrg } from '@/utils/permissions/helpers';
import { tv } from '@/utils/styles';

import { extraNavButtonTextVariants } from '../utils/transitions';

interface Props {
  isOpen: boolean;
  session?: Session | null;
  visibility: string;
  userAuthOn: boolean;
}

const getSubscriptionPlanText = (subscription?: Session['subscription']) => {
  if (subscription && subscription.planName !== 'Starter') return subscription.planName;
  return 'Free plan';
};

const UserPopover = ({ isOpen, session, visibility, userAuthOn }: Props) => {
  const [userNavIsOpen, setUserNavIsOpen] = useState<boolean>(false);
  const userPlanName = getSubscriptionPlanText(session?.subscription);
  const userOrgName = session ? getUserOrg(session?.user)?.name : undefined;
  const hasMultipleOrgs = getActiveUserOrgsCount(session?.user) > 1;

  const { userNavAvatar, userNavTrigger, userDetails, userName, popoverContent, background, triggerIcon } = styles({
    isOpen,
  });

  if (!session) return null;

  return (
    <Popover.Root onOpenChange={() => setUserNavIsOpen(!userNavIsOpen)}>
      {userAuthOn && (
        <Popover.Trigger className={userNavTrigger()}>
          <div className={userNavAvatar()}>
            <UserAvatar user={session.user} />
          </div>

          <motion.span variants={extraNavButtonTextVariants} animate={visibility} initial={visibility}>
            <div className={userDetails()}>
              <Text variant="secondary" className="mb-0.5 leading-none">
                <span className={userName()}>{session.user.name}</span>
              </Text>
              <Badge className="block max-w-full truncate" variant="alert">
                {hasMultipleOrgs ? userOrgName : userPlanName}
              </Badge>
            </div>
            <span className={background()} />
          </motion.span>
          <ChevronsUpDown className={triggerIcon()} />
        </Popover.Trigger>
      )}
      <AnimatePresence>
        {userNavIsOpen && session && (
          <Popover.Portal>
            <Popover.Content
              sideOffset={10}
              alignOffset={-1.5}
              forceMount
              side="top"
              align="start"
              className={popoverContent()}
            >
              <UserDropdown isOpen session={session} showUpgrade />
            </Popover.Content>
          </Popover.Portal>
        )}
      </AnimatePresence>
    </Popover.Root>
  );
};

const styles = tv({
  slots: {
    userNavTrigger:
      'group relative my-3.5 grid w-full grid-cols-[28px_1fr] items-center gap-2 pl-0.5 md:w-auto md:grid-cols-[28px_126px]',
    userDetails: 'relative z-2 flex flex-col items-start pr-6',
    userName: 'inline-block max-w-[6.5rem] truncate',
    userNavAvatar:
      'z-2 flex size-7 items-center justify-center rounded-full border-2 border-solid bg-white transition-colors duration-200',
    background: 'absolute -bottom-2 -left-1 -right-2 -top-2 z-1 rounded-md group-hover:bg-blue-500',
    triggerIcon: 'absolute right-1.5 top-1/2 size-4 -translate-y-1/2 text-white/30 group-hover:text-lightBlue-500',
    popoverContent: 'relative z-10',
  },
  variants: {
    isOpen: {
      true: null,
      false: {
        userNavAvatar: 'hover:border-lightBlue-500',
      },
    },
  },
});

export { UserPopover };
